import * as React from "react";

// MUI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/Edit";
import VerifiedIcon from "@mui/icons-material/Verified";
import Modal from "@mui/material/Modal";
import Grid from "@mui/material/Grid";

// Third Party
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";

// Internal
import SharedAppBar from "../../components/SharedAppBar";
import SharedCompose from "../../components/SharedCompose";
import en from "../../data/en.json";
import withRoot from "../../withRoot";
import {
  GET_USER_PROFILE_BY_HANDLE,
  GET_USER_COLLECTIONS,
} from "../../data/graphql/queries";
import { FOLLOW_USER, UPDATE_POST } from "../../data/graphql/mutations";
import {
  PostType,
  UserType,
  PublicationType,
  TagType,
  CollectionType,
  BadgeType,
} from "../../data/types";

function Main() {
  let { handle } = useParams();
  const { user: auth0User, isAuthenticated } = useAuth0();
  const [followUser, { data: FollowUserData }] = useMutation(FOLLOW_USER);
  const [updatePost, { data: _UpdatePostData }] = useMutation(UPDATE_POST);
  const { loading, error, data } = useQuery(GET_USER_PROFILE_BY_HANDLE, {
    variables: { handle: handle },
    errorPolicy: "all",
    onError: (error) => {
      console.error("GraphQL Error in user profile:", {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        extraInfo: error.extraInfo,
      });
    },
    onCompleted: (data) => {
      console.log("User profile data loaded successfully:", data);
    },
  });

  const [tab, setTab] = React.useState<string>("1");
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };
  const [user, setUser] = React.useState<UserType | null>(null);
  const [editPost, setEditPost] = React.useState<PostType | null>(null);

  const {
    loading: collectionsLoading,
    error: collectionsError,
    data: collectionsData,
  } = useQuery(GET_USER_COLLECTIONS, {
    variables: {
      user_auth0_id: user?.auth0Id,
      include_private: false, // Only show public collections on profile page
    },
    skip: !user?.auth0Id, // Skip query until we have user data
  });

  React.useEffect(() => {
    console.log("Main.tsx useEffect triggered with data:", data);
    if (data && data.userProfileByHandle) {
      const u = data.userProfileByHandle as UserType;
      console.log("Setting Main.tsx user data:", {
        userId: u.id,
        handle: u.handle,
        hasBadges: !!u.badges,
        badgesCount: u.badges?.length || 0,
        profile: u.profile,
      });
      setUser(u);
    } else if (data) {
      console.warn("Main.tsx user profile data is null for handle:", handle);
    }
  }, [data, handle]);

  React.useEffect(() => {
    if (FollowUserData) {
      setUser(FollowUserData.followUser as UserType);
    }
  }, [FollowUserData]);

  // Add logging for current state
  console.log("Main.tsx render state:", {
    loading,
    error: !!error,
    user: !!user,
    data: !!data,
  });

  if (loading || !user) {
    console.log("Main.tsx is loading...");
    return <p>{en.common["loading.."]}</p>;
  }
  if (error) {
    console.error("Main.tsx error:", error);
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Public Profile Error: Unable to load user profile
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Error Message: {error.message}
        </Typography>
        {error.graphQLErrors?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">GraphQL Errors:</Typography>
            {error.graphQLErrors.map((err, index) => (
              <Typography key={index} variant="body2" color="text.secondary">
                • {err.message}
              </Typography>
            ))}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          User Handle: {handle}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const handleFollowUser = (
    userAuth0Id: string | undefined,
    followingUserHandle: string
  ) => {
    if (userAuth0Id) {
      followUser({
        variables: {
          user_auth0_id: userAuth0Id,
          following_user_handle: followingUserHandle,
        },
      });
    }
  };

  const handleUpdatePost = (
    postId: string,
    text: string,
    imageUrl: string | null
  ) => {
    if (auth0User) {
      updatePost({
        variables: {
          post_id: postId,
          user_auth0_id: auth0User.sub,
          text: text,
          image_url: imageUrl,
        },
      });
    }
    setEditPost(null);
  };

  const renderFollowButton = () => {
    if (!isAuthenticated || (auth0User && auth0User.sub === user.auth0Id)) {
      return null;
    }
    const foundUser = user.followers.find(
      (follower: UserType) => follower.auth0Id === auth0User?.sub
    );
    const buttonText = foundUser
      ? en.userProfile.unfollow
      : en.userProfile.follow;
    return (
      <Button
        variant="contained"
        onClick={() => handleFollowUser(auth0User?.sub, user.handle)}
      >
        {buttonText}
      </Button>
    );
  };

  const renderEditPostModal = (user: UserType, post: PostType) => {
    return (
      <Modal open={editPost !== null} onClose={() => setEditPost(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "50%",
            height: "50%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <SharedCompose
            user={user}
            post={post}
            handleUpdatePost={handleUpdatePost}
          />
        </Box>
      </Modal>
    );
  };

  const renderPosts = (posts: PostType[]) => {
    return (
      <React.Fragment>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
            gap: "1rem",
          }}
        >
          {editPost && renderEditPostModal(user, editPost)}
          {posts.map((post: PostType, index: number) => (
            <Box key={index}>
              <Box display="flex" flexDirection="row" alignItems="center">
                <Avatar src={user.profile?.photo?.url} />
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", pr: 1, pl: 1 }}
                >
                  {user.profile?.nickname}
                </Typography>
                <Typography variant="body1" sx={{ pr: 1 }}>
                  {post.createdAt}
                </Typography>
                <IconButton onClick={() => setEditPost(post)}>
                  <EditIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  maxHeight: 250,
                  pt: 1,
                  pb: 1,
                  overflow: "hidden",
                  overflowY: "scroll",
                }}
              >
                <Typography variant="body2">{post.text}</Typography>
              </Box>
              {post.imageUrl && (
                <img
                  alt={en.community.postImage}
                  src={post.imageUrl}
                  width={"100%"}
                  height={"auto"}
                />
              )}
            </Box>
          ))}
        </div>
      </React.Fragment>
    );
  };

  const renderPublications = (publications: PublicationType[]) => {
    return (
      <React.Fragment>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
            gap: "1rem",
          }}
        >
          {publications.map((publication: PublicationType, index: Number) => (
            <Link
              key={publication.uuid}
              to={`/publications/${publication.uuid}`}
              style={{ textDecoration: "none", color: "black" }}
            >
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                  height: "auto",
                  minHeight: 200,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    lineHeight: 1.3,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {publication.title}
                </Typography>

                {publication.headline && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {publication.headline}
                  </Typography>
                )}

                <Box sx={{ mt: "auto", pt: 1 }}>
                  <Box
                    sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}
                  >
                    {publication.tags
                      .slice(0, 3)
                      .map((tag: TagType, index: number) => (
                        <Chip
                          key={index}
                          label={tag.name}
                          variant="outlined"
                          size="small"
                          sx={{ fontSize: "0.75rem" }}
                        />
                      ))}
                    {publication.tags.length > 3 && (
                      <Chip
                        label={`+${publication.tags.length - 3} more`}
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: "0.75rem" }}
                      />
                    )}
                  </Box>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Status: {publication.status}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Link>
          ))}
        </div>
      </React.Fragment>
    );
  };

  const renderBadges = (badges: BadgeType[]) => {
    if (!badges || badges.length === 0) {
      return null;
    }

    return (
      <Box
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        sx={{ pt: 0.5, pb: 1, flexWrap: "wrap", gap: 1 }}
      >
        {badges.map((badge: BadgeType) => (
          <Chip
            key={badge.id}
            label={badge.name}
            variant="outlined"
            size="medium"
            avatar={
              badge.imageUrl ? (
                <Avatar
                  src={badge.imageUrl}
                  sx={{
                    width: 28,
                    height: 28,
                    marginLeft: "8px !important",
                    marginRight: "-4px !important",
                  }}
                />
              ) : undefined
            }
            sx={{
              backgroundColor: "white",
              color: "#2B2D6A",
              borderColor: "#2B2D6A",
              borderWidth: "2px",
              fontWeight: "bold",
              fontSize: "0.875rem",
              display: "flex",
              alignItems: "center",
              height: "36px",
              minHeight: "36px",
              maxHeight: "36px",
              borderRadius: "18px",
              boxShadow: "0 2px 4px rgba(43, 45, 106, 0.1)",
              transition: "all 0.2s ease-in-out",
              "& .MuiChip-label": {
                paddingLeft: "12px",
                paddingRight: "12px",
                lineHeight: "1.2",
                fontWeight: "600",
              },
              "& .MuiChip-avatar": {
                width: "28px",
                height: "28px",
                marginLeft: "8px",
                marginRight: "-4px",
              },
              "&:hover": {
                backgroundColor: "#f8f9ff",
                borderColor: "#2B2D6A",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 8px rgba(43, 45, 106, 0.15)",
              },
            }}
          />
        ))}
      </Box>
    );
  };

  const renderGalleries = (collections: CollectionType[]) => {
    return (
      <React.Fragment>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "1rem",
          }}
        >
          {collections.map((collection: CollectionType) => (
            <Link
              key={collection.id}
              to={`/collections/${collection.uuid}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  {collection.title}
                </Typography>
                {collection.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {collection.description}
                  </Typography>
                )}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    {collection.itemsCount} items
                  </Typography>
                  <Chip
                    label={collection.public ? "Public" : "Private"}
                    size="small"
                    variant={collection.public ? "outlined" : "filled"}
                    color={collection.public ? "primary" : "default"}
                  />
                </Box>
              </Box>
            </Link>
          ))}
        </div>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <SharedAppBar returnUrl="/" />
      <Grid
        container
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
        sx={{ mt: 10, pt: 5 }}
      >
        <Grid item xs={2} />
        <Grid item xs={8} sx={{ width: 750 }}>
          <Box display="flex" flexDirection="row" alignItems="center">
            <Avatar
              sx={{ mr: 1 }}
              alt="userProfile"
              src={user.profile.photo?.url}
            />
            <Typography variant="h5" sx={{ mr: 1 }}>
              {user.profile.nickname}
            </Typography>
            {user.profile.pronouns && (
              <Typography variant="h5" sx={{ mr: 1 }}>
                ({user.profile.pronouns})
              </Typography>
            )}
            <Typography variant="h5" sx={{ mr: 1 }}>
              @{user.handle}
            </Typography>
            {user.verified || (true && <VerifiedIcon />)}
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            sx={{ pt: 2, pb: 0 }}
          >
            {user.profile.twitter && (
              <Button component={Link} to={user.profile.twitter} variant="text">
                <TwitterIcon />
              </Button>
            )}
            {user.profile.instagram && (
              <Button
                component={Link}
                to={user.profile.instagram}
                variant="text"
              >
                <InstagramIcon />
              </Button>
            )}
            <Button
              component={Link}
              to={`/users/${user.handle}`}
              variant="text"
            >
              <LinkIcon />
            </Button>
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            sx={{ pt: 2, pb: 0, m: 2 }}
          >
            {user.profile?.bio && (
              <Typography variant="body1">{user.profile.bio}</Typography>
            )}
          </Box>

          {/* Badges Section */}
          <Box sx={{ m: 2 }}>
            {(() => {
              console.log("Main.tsx badges render check:", {
                hasBadges: !!user.badges,
                badgesLength: user.badges?.length,
                badges: user.badges,
              });
              try {
                return user.badges && renderBadges(user.badges);
              } catch (badgeError) {
                console.error("Badge rendering error in Main.tsx:", badgeError);
                return (
                  <Typography variant="body2" color="text.secondary">
                    Badges temporarily unavailable
                  </Typography>
                );
              }
            })()}
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            sx={{ pt: 2, pb: 2 }}
          >
            <Typography variant="body1" sx={{ mr: 1 }}>
              {user.followers.length} {en.userProfile.follower}
            </Typography>
            <Typography variant="body1" sx={{ mr: 4 }}>
              {user.following.length} {en.userProfile.following}
            </Typography>
            {renderFollowButton()}
          </Box>

          <Box sx={{ width: "100%", typography: "body1" }}>
            <TabContext value={tab}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList onChange={handleTabChange}>
                  <Tab label={en.common.posts} value="1" />
                  <Tab label={en.common.publications} value="2" />
                  <Tab label={en.common.galleries} value="3" />
                </TabList>
              </Box>
              <TabPanel value="1">
                {user.posts.length === 0 && en.userProfile.noPosts}
                {user.posts.length > 0 && renderPosts(user.posts)}
              </TabPanel>
              <TabPanel value="2">
                {user.publications.length === 0 &&
                  en.userProfile.noPublications}
                {user.publications.length > 0 &&
                  renderPublications(user.publications)}
              </TabPanel>
              <TabPanel value="3">
                {collectionsLoading && <p>{en.common["loading.."]}</p>}
                {collectionsError && (
                  <p>
                    {en.common.error}: {collectionsError.message}
                  </p>
                )}
                {!collectionsLoading && !collectionsError && (
                  <>
                    {(!collectionsData?.userCollections ||
                      collectionsData.userCollections.length === 0) &&
                      "No galleries yet"}
                    {collectionsData?.userCollections &&
                      collectionsData.userCollections.length > 0 &&
                      renderGalleries(collectionsData.userCollections)}
                  </>
                )}
              </TabPanel>
            </TabContext>
          </Box>
        </Grid>
        <Grid item xs={2} />
      </Grid>
    </React.Fragment>
  );
}

export default withRoot(Main);
